import { useCallback, useEffect, useRef, useState } from 'react';
import { useListNamespacesQuery } from 'src/generated/graphql';
import { computePagesCount } from 'src/common/utils/pagination';

/** Page size — matches backend DEFAULT_PAGE_SIZE (25). 1:1 page-to-fetch. */
export const NAMESPACE_UI_PAGE_SIZE = 25;

export interface UseNamespaceListResult {
  /** Namespace ids for the current UI page. */
  namespaceIds: string[];
  currentPage: number;
  /** Number of known pages (grows as the user navigates forward). */
  pagesCount: number;
  /** True when more pages exist beyond the current one (server has a nextToken). */
  hasMore: boolean;
  loading: boolean;
  error?: Error;
  /** Count of namespaces on the current page (0 when empty). */
  totalLoaded: number;
  goToPage: (page: number) => void;
}

/**
 * Fetches namespaces one page at a time with a 1:1 page-to-fetch mapping.
 * Each page transition changes the query variables (nextToken), causing Apollo
 * to declaratively refetch. No client-side buffer or slicing needed.
 *
 * Follows the same pagination pattern as useNamespacePageData.ts.
 */
export function useNamespaceList(searchFilter: string): UseNamespaceListResult {
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [pageTokens, setPageTokens] = useState<(string | null)[]>([null]);

  // Tracks the `data` reference that existed when the most recent filter-
  // change reset occurred. If the token-capture effect sees this same data
  // reference, it's stale (predates the current pagination context) and must
  // not write to pageTokens.
  //
  // WHY THIS WORKS: Apollo returns a new object reference for each distinct
  // response. After a reset, the token-capture effect ignores any data that
  // matches the reference captured at reset time. When the fresh response
  // arrives, `data` becomes a new reference (!== staleDataRef) → write allowed.
  const staleDataRef = useRef<unknown>(null);
  const dataRef = useRef<unknown>(null);
  const isMountRef = useRef(true);

  // Reset pagination when the search filter changes.
  // On mount, skip stale-marking since the initial data is valid.
  useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }
    setCurrentPageIndex(1);
    setPageTokens([null]);
    // Mark the current data as stale
    staleDataRef.current = dataRef.current;
  }, [searchFilter]);

  const { data, loading, error } = useListNamespacesQuery({
    variables: {
      namespaceIdPrefix: searchFilter || undefined,
      pageSize: NAMESPACE_UI_PAGE_SIZE,
      nextToken: pageTokens[currentPageIndex - 1],
    },
    notifyOnNetworkStatusChange: true,
  });

  // Keep a ref to the current data so the reset effect can capture it
  dataRef.current = data;

  // Capture the response's nextToken for forward navigation.
  // Guards:
  //   1. !loading — don't write during refetch transitions
  //   2. stale data check — if this data reference is the same one that was
  //      present when the reset fired, it's from a prior filter context
  useEffect(() => {
    if (!data || loading) return;

    // Stale data guard
    if (data === staleDataRef.current) return;

    const responseToken = data.listNamespaces.nextToken ?? null;
    setPageTokens((prev) => {
      const updated = [...prev];
      updated[currentPageIndex] = responseToken;
      // Trim stale tokens beyond current page when there's no next page
      if (!responseToken) {
        return updated.slice(0, currentPageIndex + 1);
      }
      return updated;
    });
  }, [data, currentPageIndex, loading]);

  const namespaceIds = (data?.listNamespaces.namespaces ?? []).map(
    (ns) => ns.namespaceId,
  );
  const hasNextToken = !!data?.listNamespaces.nextToken;

  const goToPage = useCallback((page: number) => {
    setCurrentPageIndex(page);
  }, []);

  return {
    namespaceIds,
    currentPage: currentPageIndex,
    pagesCount: computePagesCount(pageTokens, hasNextToken, currentPageIndex),
    hasMore: hasNextToken,
    loading,
    error,
    totalLoaded: namespaceIds.length,
    goToPage,
  };
}
